import { Injectable } from '@angular/core';
import { extractColors } from 'extract-colors';
import { GRADIENT_NAMES } from '../../data/gradient/gradient-name.data';
import { THEMES } from '../../data/product/product.data';
import { Product } from '../../model/product/product.model';
import { ColorService } from '../color/color.service';

export interface GeneratedPalette {
  hexes: string[];
  scheme: string;
  // Named-colour slug the palette was seeded from (harmony / analogous /
  // monochrome schemes only).
  initializer?: string;
  // Base36 seed for a full-random palette: the same seed always reproduces the
  // exact same palette, and it is shown as the Source.
  seed?: string;
}

export interface RandomStart {
  hexes: string[];
  // Exactly one of these describes the source: a theme, or a generated scheme.
  theme?: Product;
  scheme?: string;
  initializer?: string;
  seed?: string;
}

export interface ImagePalette {
  hexes: string[];
  // A resized data-URL of the source image, plus its file name.
  src: string;
  id: string;
}

// Per-scheme colour-count floors, one set per tool, so the random start can draw
// a count inside each generator's valid range (instead of always 10). These match
// the count rules the gradient / palette widgets enforce in their UI.
export interface SchemeMins {
  defaultMin: number;
  schemeMins: { [ key: string ]: number };
}

// Gradient: only two stops are needed, so most generators floor at 2; the multi-
// anchor harmonies need more. (Mirrors the gradient widget's schemeMins.)
/** the colour count a harmony scheme cannot go under, enforced inside build() for every tool */
export const HARMONY_FLOORS: { [ key: string ]: number } = { 'split complementary': 3, triadic: 3, tetradic: 4 };

export const GRADIENT_SCHEME_MINS: SchemeMins = {
  defaultMin: 2,
  schemeMins: { 'split complementary': 3, triadic: 3, tetradic: 4 },
};

// Palette: a usable palette floors at 3; tetradic needs 4. (Mirrors the palette
// widget's defaultMin / schemeMins.)
export const PALETTE_SCHEME_MINS: SchemeMins = {
  defaultMin: 3,
  schemeMins: { 'split complementary': 3, triadic: 3, tetradic: 4 },
};

// Shared palette generation, so the palette page and the home-page widget build
// identical palettes. Nothing here navigates or touches the DOM beyond the
// image canvas helper.
@Injectable( { providedIn: 'root' } )
export class PaletteGenerationService {

  constructor( private colorService: ColorService ) {
  }

  // Build a 10-colour palette for a scheme seeded from a SPECIFIC source colour
  // (used by the color tool's palette examples). Returns [] for full-random,
  // which has no source colour.
  paletteFromColor( baseHex: string, scheme: string ): string[] {
    switch ( scheme ) {
      case 'complementary':
        return this.harmonyAround( baseHex, [ 0, 180 ], 10 );
      case 'split complementary':
        return this.harmonyAround( baseHex, [ 0, 150, 210 ], 10 );
      case 'triadic':
        return this.harmonyAround( baseHex, [ 0, 120, 240 ], 10 );
      case 'tetradic':
        return this.harmonyAround( baseHex, [ 0, 90, 180, 270 ], 10 );
      case 'analogous':
        return this.colorService.generateAnalogousColors( baseHex, 10 );
      case 'monochrome':
        return this.colorService.generateMonochromeColors( baseHex, 10 );
      case 'warm':
        return this.temperatureFrom( baseHex, true );
      case 'cool':
        return this.temperatureFrom( baseHex, false );
      default:
        return [];
    }
  }

  // Build a 10-colour palette for a named scheme.
  build( scheme: string, count = 10 ): GeneratedPalette {
    // Floor of 2 (a gradient can have two stops); each tool's UI enforces its own
    // higher per-generator minimum before calling this.
    // a hard floor per scheme, whatever a UI asked for: a triadic palette has at least its three
    // hues, a tetradic its four, a split complementary its three (owner, 2026-09-22)
    const floor = HARMONY_FLOORS[ scheme ] || 2;
    const n = Math.max( floor, Math.min( 10, count ) );
    switch ( scheme ) {
      case 'complementary':
        return this.harmony( [ 0, 180 ], scheme, n );
      case 'split complementary':
        return this.harmony( [ 0, 150, 210 ], scheme, n );
      case 'triadic':
        return this.harmony( [ 0, 120, 240 ], scheme, n );
      case 'tetradic':
        return this.harmony( [ 0, 90, 180, 270 ], scheme, n );
      case 'analogous':
        return this.fromNamed( h => this.colorService.generateAnalogousColors( h, n ), scheme );
      case 'monochrome':
        return this.fromNamed( h => this.colorService.generateMonochromeColors( h, n ), scheme );
      case 'warm':
        return this.temperatureSeeded( true, n );
      case 'cool':
        return this.temperatureSeeded( false, n );
      default:
        return this.fullRandom( undefined, n );
    }
  }

  // A full-random palette built from a seed. A given seed always reproduces the
  // same palette, so the seed can be shared/shown as the Source. With no seed a
  // fresh random one is drawn.
  fullRandom( seed?: string, count = 10 ): GeneratedPalette {
    const seedStr = seed && /^[0-9a-z]+$/i.test( seed )
      ? seed.toLowerCase()
      : this.randomSeed();
    return { hexes: this.full( parseInt( seedStr, 36 ) >>> 0, count ), scheme: 'full', seed: seedStr };
  }

  // A fresh seed: 16 all-lowercase letters. Shown as the Source for a random
  // palette and round-trips through the base36 parse above.
  private randomSeed(): string {
    let s = '';
    for ( let i = 0; i < 16; i++ ) {
      s += String.fromCharCode( 97 + Math.floor( Math.random() * 26 ) );
    }
    return s;
  }

  // A stable 16-char lowercase seed FINGERPRINT for an arbitrary (e.g. user-edited)
  // palette, so any custom variation can still be referenced as a "Seed". Purely a
  // deterministic label (same colours -> same seed), not a reproducer.
  paletteSeed( hexes: string[] ): string {
    const str = ( hexes || [] ).map( x => ( x || '' ).replace( '#', '' ).toLowerCase() ).join( '' );
    let h = 0x811c9dc5;
    for ( let i = 0; i < str.length; i++ ) {
      h ^= str.charCodeAt( i );
      h = Math.imul( h, 0x01000193 );
    }
    const rnd = this.seededRandom( h >>> 0 );
    let out = '';
    for ( let i = 0; i < 16; i++ ) {
      out += String.fromCharCode( 97 + Math.floor( rnd() * 26 ) );
    }
    return out;
  }

  // A small deterministic PRNG (mulberry32) so a seed reproduces a palette.
  private seededRandom( seed: number ): () => number {
    let a = seed >>> 0;
    return () => {
      a = ( a + 0x6D2B79F5 ) | 0;
      let t = Math.imul( a ^ ( a >>> 15 ), 1 | a );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  // The two-colour keys of the curated open-data gradient set, cached on first use.
  private featuredKeys: string[] | null = null;

  // A random two-stop gradient from the curated open-data set (GRADIENT_NAMES),
  // returned with its name. Only two-colour entries are used — the gradient tool's
  // Featured generator locks to two stops. The colours carry their own name, looked
  // up later via ColorService.getColorSetName for the subtitle.
  featuredGradient(): { hexes: string[]; name: string } {
    if ( !this.featuredKeys ) {
      this.featuredKeys = Object.keys( GRADIENT_NAMES ).filter( k => k.split( '-' ).length === 2 );
    }
    if ( !this.featuredKeys.length ) {
      return { hexes: [], name: '' };
    }
    const key = this.featuredKeys[ Math.floor( Math.random() * this.featuredKeys.length ) ];
    return { hexes: key.split( '-' ).map( h => '#' + h ), name: GRADIENT_NAMES[ key ] };
  }

  // A random Codigrate theme as a starting palette.
  /** one named theme's palette, for a link that says which theme it came from */
  themeByKey( key: string | null | undefined ): RandomStart {
    if ( !key ) return { hexes: [], theme: undefined as any };
    const all = THEMES.map( line => line.products ).reduce( ( a, b ) => a.concat( b ), [] as Product[] );
    const theme = all.find( t => t.key === key && t.palette && t.palette.length >= 3 );
    const hexes = theme ? theme.palette.slice( 0, 10 ).map( h => ( h.startsWith( '#' ) ? h : '#' + h ).toLowerCase() ) : [];
    return { hexes, theme: theme as any };
  }

  /** a random theme; never `exclude`, the one on screen, so every Generate on Random Theme brings another one
   *  (owner 2026-10-12) */
  randomTheme( exclude?: string ): RandomStart {
    const all = THEMES.map( line => line.products ).reduce( ( a, b ) => a.concat( b ), [] as Product[] );
    const usable = all.filter( t => t.palette && t.palette.length >= 3 );
    const others = usable.filter( t => t.key !== exclude );
    const valid = others.length ? others : usable;
    const theme = valid[ Math.floor( Math.random() * valid.length ) ];
    const hexes = theme ? theme.palette.slice( 0, 10 ).map( h => ( h.startsWith( '#' ) ? h : '#' + h ).toLowerCase() ) : [];
    return { hexes, theme };
  }

  // A random starting palette, used identically by the palette page (on a blank
  // visit) and the home widget seed: half the time a random theme, half the time
  // a random generated scheme. When `mins` is given, the scheme's colour count is
  // a RANDOM value inside that scheme's valid range on the calling tool (e.g.
  // gradient triadic = 3..10), instead of always 10. Themes keep their own length.
  randomStart( mins?: SchemeMins ): RandomStart {
    if ( Math.random() < 0.5 ) {
      const t = this.randomTheme();
      if ( t.hexes.length ) { return t; }
    }
    const schemes = [ 'complementary', 'split complementary', 'triadic', 'tetradic', 'analogous', 'monochrome', 'warm', 'cool', 'full' ];
    const scheme = schemes[ Math.floor( Math.random() * schemes.length ) ];
    const min = mins ? ( mins.schemeMins[ scheme ] ?? mins.defaultMin ) : 10;
    const count = min + Math.floor( Math.random() * ( 10 - min + 1 ) );
    return this.build( scheme, count );
  }

  private fromNamed( gen: ( hex: string ) => string[], scheme: string ): GeneratedPalette {
    const initializer = this.colorService.getRandomNamedColor();
    const hexes = gen( this.colorService.getHexCode( initializer ) );
    return { hexes, scheme, initializer };
  }

  private harmony( offsets: number[], scheme: string, count = 10 ): GeneratedPalette {
    const initializer = this.colorService.getRandomNamedColor();
    const hexes = this.harmonyAround( this.colorService.getHexCode( initializer ), offsets, count );
    return { hexes, scheme, initializer };
  }

  // A harmony palette built AROUND the anchor hues (base + each offset), not a
  // sweep from one end to the other: each anchor hue gets its own dark-to-light
  // ramp (reversed on alternate anchors so matching lightnesses meet), and the
  // exact base colour is dropped into its natural lightness slot.
  private harmonyAround( baseHex: string, offsets: number[], n: number ): string[] {
    const base = this.colorService.hexToHsl( baseHex.replace( '#', '' ) );
    const sat = Math.min( 90, Math.max( 50, base.s ) );
    const k = offsets.length;
    const out: string[] = [];
    for ( let a = 0; a < k; a++ ) {
      const count = Math.floor( ( n - a + k - 1 ) / k );
      const hue = ( base.h + offsets[ a ] + 360 ) % 360;
      for ( let j = 0; j < count; j++ ) {
        let t = count > 1 ? j / ( count - 1 ) : 0.5;
        if ( a % 2 === 1 ) { t = 1 - t; }
        const light = Math.round( 28 + t * 56 );
        out.push( '#' + this.colorService.hslToHex( hue, sat, light ) );
      }
    }
    const baseNorm = baseHex.startsWith( '#' ) ? baseHex.toLowerCase() : '#' + baseHex.toLowerCase();
    const count0 = Math.floor( ( n + k - 1 ) / k );
    let bestIdx = 0;
    let bestDiff = Infinity;
    for ( let j = 0; j < count0; j++ ) {
      const t = count0 > 1 ? j / ( count0 - 1 ) : 0.5;
      const diff = Math.abs( ( 28 + t * 56 ) - base.l );
      if ( diff < bestDiff ) { bestDiff = diff; bestIdx = j; }
    }
    if ( out.length ) { out[ bestIdx ] = baseNorm; }
    return out;
  }

  // The warm / cool hue band test (centre + half-width), shared by the seed
  // picker and the palette builder so they always agree on what counts as warm.
  private inTemperatureBand( hue: number, warm: boolean ): boolean {
    const center = warm ? 15 : 205;
    const half = warm ? 45 : 55;
    return Math.abs( ( ( hue - center + 540 ) % 360 ) - 180 ) <= half;
  }

  // A warm/cool palette seeded from a random named colour, so it has a source.
  // Keep drawing a seed until it is actually the right temperature: a warm
  // palette is seeded from a warm colour, a cool palette from a cool one (no
  // forcing a cool colour into a warm palette).
  private temperatureSeeded( warm: boolean, count = 10 ): GeneratedPalette {
    let initializer = this.colorService.getRandomNamedColor();
    for ( let tries = 0; tries < 30; tries++ ) {
      const hue = this.colorService.hexToHsl( this.colorService.getHexCode( initializer ).replace( '#', '' ) ).h;
      if ( this.inTemperatureBand( hue, warm ) ) { break; }
      initializer = this.colorService.getRandomNamedColor();
    }
    const hexes = this.temperatureFrom( this.colorService.getHexCode( initializer ), warm, count );
    return { hexes, scheme: warm ? 'warm' : 'cool', initializer };
  }

  // A warm or cool palette whose colours ALL stay inside the warm / cool hue
  // band, so a warm palette is never accidentally built from cool hues (or vice
  // versa). Hues fan out within the band, biased toward the source when it is
  // already in-band; when the source is the wrong temperature it is pulled to
  // the nearest band edge so nothing leaks out. Saturation comes from the
  // source, lightness ramps, and the set is ordered dark -> light.
  private temperatureFrom( baseHex: string, warm: boolean, count = 10 ): string[] {
    const hsl = this.colorService.hexToHsl( baseHex.replace( '#', '' ) );
    const sat = Math.min( 85, Math.max( 45, hsl.s ) );
    // Warm band: ~ 330..60 around red-orange (reds, oranges, yellows; never into
    // yellow-green). Cool band: ~ 150..260 (greens, cyans, blues). Centre + half.
    const center = warm ? 15 : 205;
    const half = warm ? 45 : 55;
    // Signed offset of the source hue from the band centre (-180..180).
    const srcOff = ( ( hsl.h - center + 540 ) % 360 ) - 180;
    // Anchor inside the band: the source's own offset when it fits, otherwise
    // pulled to the nearest edge so the palette stays warm/cool.
    const anchorOff = Math.max( -half, Math.min( half, srcOff ) );
    const n = Math.max( 2, count );
    const out: string[] = [];
    for ( let i = 0; i < n; i++ ) {
      const t = i / ( n - 1 );
      // Fan around the anchor across most of the band, clamped to never leave it.
      const off = Math.max( -half, Math.min( half, anchorOff + ( t - 0.5 ) * 2 * half * 0.8 ) );
      const hue = ( center + off + 360 ) % 360;
      const light = Math.round( 26 + t * 58 );                 // 26..84 ramp
      out.push( '#' + this.colorService.hslToHex( hue, sat, light ) );
    }
    // Always drop the exact source colour into the slot whose lightness is
    // closest to it, so a warm/cool palette ALWAYS contains its source. (The
    // seed picker already guarantees the source is in-band, so this never leaks
    // an off-temperature colour in.)
    const baseNorm = baseHex.startsWith( '#' ) ? baseHex.toLowerCase() : '#' + baseHex.toLowerCase();
    let bestIdx = 0, bestDiff = Infinity;
    for ( let i = 0; i < n; i++ ) {
      const diff = Math.abs( ( 26 + ( i / ( n - 1 ) ) * 58 ) - hsl.l );
      if ( diff < bestDiff ) { bestDiff = diff; bestIdx = i; }
    }
    out[ bestIdx ] = baseNorm;
    // Order dark -> light for a smooth, aesthetic gradient.
    return out
      .map( v => ( { value: v, l: this.colorService.hexToHsl( v.replace( '#', '' ) ).l } ) )
      .sort( ( a, b ) => a.l - b.l )
      .map( x => x.value );
  }

  private full( seed: number, count = 10 ): string[] {
    const rnd = this.seededRandom( seed );
    const items: { value: string }[] = [];
    for ( let i = 0; i < count; i++ ) {
      const n = Math.floor( rnd() * 0x1000000 );
      items.push( { value: '#' + n.toString( 16 ).padStart( 6, '0' ) } );
    }
    this.colorService.sortByHue( items );
    return items.map( i => i.value );
  }

  // Extract up to 10 dominant colours from an image file and return them along
  // with a resized data-URL of the image for the hero backdrop.
  buildFromImage( file: File ): Promise<ImagePalette> {
    return new Promise( ( resolve, reject ) => {
      const reader = new FileReader();
      reader.onload = ( e: any ) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = async () => {
          try {
            const options = { pixels: 500, distance: 0.1, saturationDistance: 0, lightnessDistance: 0, hueDistance: 0 };
            const colors = await extractColors( img.src, options );
            colors.sort( ( a, b ) => b.area - a.area );
            let hexes = colors.map( c => c.hex );
            if ( hexes.length > 10 ) { hexes = hexes.slice( 0, 10 ); }
            while ( hexes.length > 0 && hexes.length < 3 ) { hexes.push( hexes[ 0 ] ); }

            const maxWidth = 800;
            const maxHeight = 600;
            let width = img.width;
            let height = img.height;
            if ( width > maxWidth || height > maxHeight ) {
              if ( width / height > maxWidth / maxHeight ) {
                height = ( maxWidth / width ) * height;
                width = maxWidth;
              } else {
                width = ( maxHeight / height ) * width;
                height = maxHeight;
              }
            }
            const canvas = document.createElement( 'canvas' );
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext( '2d' );
            const src = ctx ? ( ctx.drawImage( img, 0, 0, width, height ), canvas.toDataURL( 'image/jpeg', 0.8 ) ) : img.src;

            resolve( { hexes, src, id: file.name } );
          } catch ( error ) {
            reject( error );
          }
        };
      };
      reader.readAsDataURL( file );
    } );
  }
}
